<template>
  <page-outline :title="$t('Webheader.mandate_details')">
    <template slot="pageContent">
      <b-container fluid>
        <b-row class="min-w-100 align-items-center mt-3">
          <b-col class="d-flex justify-content-start">
            <back-button class="btnFilter_2 mr-2" :returnUrl="returnUrl"></back-button>
            <b-button class="back-button btnFilter_2 mr-2" @click="onRefresh">
              <i class="fa-solid fa-rotate-right"></i>
              {{ $t('common.rfrsh') }}
            </b-button>
          </b-col>
          <b-col class="d-flex justify-content-end">
            <!-- Cas 1 : status = 5 => boutons actions -->
            <template v-if="showAddButtons">
              <b-button
                v-if="signedPdfBase64"
                class="back-button btnFilter_2 mr-2"
                @click="sendSignedPdfCopy"
              >
                <i class="fa-solid fa-envelope"></i>
                {{ $t('mandate.send_signed_pdf_copy') }}
              </b-button>
              <b-button
                  class="back-button btnFilter_2 mr-2"
                  @click="exportAuditTrailPdf"
                >
                  <i class="fa-light fa-file-pdf"></i>
                {{ $t('mandate.export_detail_pdf') }}
              </b-button>
              <b-button v-if="details.type === 2"
                class="back-button btnFilter_2 mr-2"
                @click="newPaymentSchedule"
              >
                <i class="fa-solid fa-plus"></i>
                {{ $t('mandate.add_payment_schedule') }}
              </b-button>

              <b-button
                class="back-button btnFilter_2"
                @click="newTransaction"
              >
                <i class="fa-solid fa-plus"></i>
                {{ $t('mandate.add_collection') }}
              </b-button>
            </template>

            <!-- Cas 2 : status = 0 ou 6 => bouton resend email -->
            <template v-else-if="showResendSignatureButton">
              <b-button
                v-if="signedPdfBase64"
                class="back-button btnFilter_2 mr-2"
                @click="sendSignedPdfCopy"
              >
                <i class="fa-solid fa-envelope"></i>
                {{ $t('mandate.send_signed_pdf_copy') }}
              </b-button>
              <b-button
                  class="back-button btnFilter_2 mr-2"
                  @click="exportAuditTrailPdf"
                >
                  <i class="fa-light fa-file-pdf"></i>
                {{ $t('mandate.export_detail_pdf') }}
              </b-button>
              <b-button
                class="back-button btnFilter_2"
                @click="resendSignatureEmail"
              >
                <i class="fa-solid fa-envelope"></i>
                {{ $t('mandate.resend_sign_email') }}
              </b-button>
            </template>

            <!-- Cas 3 : autres status => rien -->
            <template v-else>
              <b-button
                v-if="signedPdfBase64"
                class="back-button btnFilter_2 mr-2"
                @click="sendSignedPdfCopy"
              >
                <i class="fa-solid fa-envelope"></i>
                {{ $t('mandate.send_signed_pdf_copy') }}
              </b-button>
              <b-button
                  class="back-button btnFilter_2 mr-2"
                  @click="exportAuditTrailPdf"
                >
                  <i class="fa-light fa-file-pdf"></i>
                {{ $t('mandate.export_detail_pdf') }}
              </b-button>
            </template>
          </b-col>
        </b-row>
        <br />

        <!-- Spinner -->
        <spinner v-if="activateSpinner"></spinner>

        <template>
          <b-row class="justify-content-center">
            <b-col cols="12" class="mt-3">
              <div class="outline-content">
                <!-- Mandate info -->
                <form-accordion :title="$t('mandate.mandate_info')" :isTitleUpperCase="false">
                  <b-row class="form-group" no-gutters>
                    <!-- UMR : éditable en mode édition -->
                    <b-col sm="4" class="mb-5">
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate.unique_mandate_rf')"
                          v-model="editable.UMR"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.unique_mandate_rf')"
                          :inputValue="details.UMR || '—'"
                        />
                      </template>
                    </b-col>

                    <!-- Transaction type : toujours en lecture -->
                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate_details.type_transaction')"
                        :inputValue="details.type ? $t('mandate_details.type_transaction_' + details.type) : '—'"
                      />
                    </b-col>


                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate_details.status')"
                        :inputValue="details.status || '—'"
                      />
                    </b-col>

                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate_details.creation_date')"
                        :inputValue="details.creation_date || '—'"
                      />
                    </b-col>

                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate_details.date_signed')"
                        :inputValue="details.signed_date || '—'"
                      />
                    </b-col>

                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate_details.cancel_date')"
                        :inputValue="details.cancelled_date || '—'"
                      />
                    </b-col>

                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate.underlying_contract_id')"
                        :inputValue="details.id_contract || '—'"
                      />
                    </b-col>

                    <b-col sm="4" class="mb-5">
                      <form-read-group-label-input
                        :inputLabel="$t('mandate_details.message_type')"
                        :inputValue="details.message_type || '—'"
                      />
                    </b-col>

                    <!-- Langue du mandat : facultative, transmise uniquement si modifiée -->
                    <b-col sm="4" class="mb-5">
                      <template v-if="isEditMode">
                        <label for="mandate-edit-language">Langue du mandat</label>
                        <select id="mandate-edit-language" class="form-control" v-model="editable.lang">
                          <option value="">Ne pas modifier</option>
                          <option v-for="option in mandateLanguageOptions" :key="option.code" :value="option.code">
                            {{ option.label }}
                          </option>
                        </select>
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          inputLabel="Langue du mandat"
                          :inputValue="mandateLanguageLabel(details.lang)"
                        />
                      </template>
                    </b-col>

                    <!-- Reason  -->
                    <b-col sm="4" class="mb-5">
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate_details.reason')"
                          v-model="editable.reason"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate_details.reason')"
                          :inputValue="details.reason"
                        />
                      </template>
                    </b-col>
                    <!-- Reject Reason -->
                    <b-col
                      v-if="details.reject_reason"
                      sm="4"
                      class="mb-5"
                    >
                      <form-read-group-label-input
                        inputLabel="Reject Reason"
                        :inputValue="details.reject_reason"
                      />
                    </b-col>
                  </b-row>
                </form-accordion>

                <hr class="mx-auto" />

                <!-- Creditor Details : éditable seulement en mode édition -->
                <form-accordion :title="$t('mandate.cdtr_info')" :isTitleUpperCase="false">
                  <b-row class="form-group" no-gutters>
                    <b-col>
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate.cdtr_name')"
                          v-model="editable.cdtr_name"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.cdtr_name')"
                          :inputValue="details.cdtr_name || '—'"
                        />
                      </template>
                    </b-col>

                    <b-col>
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('administration.identifier')"
                          v-model="editable.cdtr_identifier"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('administration.identifier')"
                          :inputValue="details.cdtr_identifier || '—'"
                        />
                      </template>
                    </b-col>

                    <b-col>
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate.cdtr_ref_name')"
                          v-model="editable.cdtr_ref"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.cdtr_ref_name')"
                          :inputValue="details.cdtr_ref || '—'"
                        />
                      </template>
                    </b-col>
                  </b-row>

                  <b-row class="form-group" no-gutters>
                    <b-col>
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate.cdtr_addrss')"
                          v-model="editable.cdtr_addr"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.cdtr_addrss')"
                          :inputValue="details.cdtr_addr || '—'"
                        />
                      </template>
                    </b-col>

                    <b-col>
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate.cdtr_city')"
                          v-model="editable.cdtr_city"
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.cdtr_city')"
                          :inputValue="details.cdtr_city || '—'"
                        />
                      </template>
                    </b-col>

                    <b-col>
                      <template v-if="isEditMode">
                        <label>{{ $t('mandate.cdtr_ctry') || 'Country of residence' }}</label>
                        <select
                          class="form-control"
                          v-model="editable.cdtr_country"
                          @change="onCdtrCountryChange"
                        >
                          <option
                            v-for="opt in countryOptions"
                            :key="opt.iso2"
                            :value="opt.iso2"
                          >
                            {{ $t('countries.' + opt.key) }}
                          </option>
                        </select>
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.cdtr_ctry')"
                          :inputValue="details.cdtr_country || '—'"
                        />
                      </template>
                    </b-col>

                  </b-row>
                </form-accordion>

                <hr class="mx-auto" />

                <!-- Debtor Details -->
                <form-accordion :title="$t('mandate.dbtr_info')" :isTitleUpperCase="false">
                  <b-row class="form-group" no-gutters>
                    <!-- Email -->
                    <b-col>
                      <template v-if="isEditMode">
                        <form-write-group-label-input
                          :inputLabel="$t('mandate.dbtr_email')"
                          v-model="editable.dbtr_email"
                          isMandatory
                        />
                      </template>
                      <template v-else>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.dbtr_email')"
                          :inputValue="details.dbtr_email || '-'"
                        />
                      </template>
                    </b-col>
                    <b-col>
                    </b-col>
                    <b-col>
                    </b-col>
                  </b-row>
                  <b-row class="form-group" no-gutters>
                    <b-col>
                      <form-read-group-label-input :inputLabel="$t('mandate.dbtr_name')" :inputValue="details.dbtr_name || '—'" />
                    </b-col>
                    <b-col>
                      <form-read-group-label-input :inputLabel="$t('mandate.dbtr_iban')" :inputValue="details.dbtr_iban || '—'" />
                    </b-col>
                    <b-col>
                      <form-read-group-label-input
                        :inputLabel="$t('mandate.dbtr_id_cd')"
                        :inputValue="details.dbtr_id || '—'"
                      />
                    </b-col>
                    </b-row>
                    <b-row class="form-group" no-gutters>
                      <b-col>
                        <form-read-group-label-input :inputLabel="$t('mandate.dbtr_addrss')" :inputValue="details.dbtr_addr || '—'" />
                      </b-col>
                      <b-col>
                        <form-read-group-label-input :inputLabel="$t('mandate.dbtr_city')" :inputValue="details.dbtr_city || '—'" />
                      </b-col>
                      <b-col>
                        <form-read-group-label-input
                          :inputLabel="$t('mandate.dbtr_country')"
                          :inputValue="details.dbtr_country || '—'"
                        />
                      </b-col>
                    </b-row>
                    <!--
                    <b-row class="form-group" no-gutters>
                      <b-col>
                        <form-read-group-label-input :inputLabel="$t('mandate.dbtr_bic')" :inputValue="details.dbtr_bic || '—'" />
                      </b-col>
                    </b-row>
                    -->
                    <b-row class="form-group" no-gutters>
                      <button
                        v-if="signedPdfBase64"
                        class="btn w-auto px-4 btn-tertiary mb-2"
                        @click.prevent="downloadSignedPdf"
                      >
                        {{ $t('mandate.signature') }}
                      </button>
                    </b-row>
                  </form-accordion>

                  <hr class="mx-auto" />

                  <!-- History -->
                  <div class="history-no-padding">
                  <form-accordion :title="$t('mandate_details.history')" :isTitleUpperCase="false">
                    <div class="table-wrap">
                      <b-table
                        striped
                        responsive
                        :items="history"
                        :fields="historyFields"
                        :empty-text="$t('common.security.no_result')"
                      />
                    </div>
                  </form-accordion>
                  </div>

                  <hr class="mx-auto" />

                  <!-- Collections -->
                  <div class="history-no-padding">
                  <form-accordion title="Collections" :isTitleUpperCase="false">
                    <div class="table-wrap">
                      <b-table
                        striped
                        responsive
                        :items="collections"
                        :fields="collectionFields"
                        empty-text="No collections found for this mandate."
                      />
                    </div>
                  </form-accordion>
                  </div>

                  <!-- Bottom buttons -->
                  <b-row
                    class="form-group justify-content-end"
                    v-if="canEditOrCancel"
                  >
                    <!-- Mode lecture -->
                    <template v-if="!isEditMode">
                      <b-col lg="2" offset-lg="8">
                        <b-button
                          variant="primary"
                          size="lg"
                          @click="enterEditMode"
                        >
                          <i class="fa-solid fa-pen-to-square" size="17px"></i>
                          <span>{{ $t('mandate.edit') }}</span>
                        </b-button>
                      </b-col>
                      <b-col sm="2">
                        <b-button
                          variant="primary"
                          @click="confirmCancelMandate"
                        >
                          <i class="fa-solid fa-xmark" size="17px"></i>
                          <span>{{ $t('mandate.cancel') }}</span>
                        </b-button>
                      </b-col>
                    </template>

                    <!-- Mode édition -->
                    <template v-else>
                      <b-col lg="2" offset-lg="8">
                        <b-button
                          variant="primary"
                          size="lg"
                          :disabled="activateSpinner"
                          @click="confirmSendEdit"
                        >
                          <i class="fa-light fa-paper-plane" size="17px"></i>
                          <span>{{ $t('mandate.send') || 'Send' }}</span>
                        </b-button>
                      </b-col>
                      <b-col sm="2">
                        <b-button
                          variant="secondary"
                          :disabled="activateSpinner"
                          @click="cancelEdit"
                        >
                        <span>{{ $t('mandate.cancel') || 'Cancel' }}</span>
                      </b-button>
                    </b-col>
                  </template>
                </b-row>
              </div>
            </b-col>
          </b-row>
        </template>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from "@/components/common/PageOutline.vue"
import BackButton from "@/components/common/backButton.vue"
import Spinner from "@/components/common/spinner.vue"
import formAccordion from "@/components/common/FormAccordion.vue"
import StatusPanelVue from "@/components/StatusPanel.vue"
import FormReadGroupLabelInput from "@/components/common/formReadGroupLabelInput.vue"
import FormWriteGroupLabelInput from "@/components/common/formWriteGroupLabelInput.vue"
//import { errorMessageAlert } from "@/helpers/alert"
import { loadPage } from "@/helpers/routing"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import moment from "moment-timezone"

export default {
  name: "MandateDetail",
  components: {
    "page-outline": PageOutline,
    "back-button": BackButton,
    spinner: Spinner,
    "form-accordion": formAccordion,
    "mandate-panel": StatusPanelVue,
    "form-read-group-label-input": FormReadGroupLabelInput,
    "form-write-group-label-input": FormWriteGroupLabelInput
  },

  data () {
    return {
      activateSpinner: false,
      IdMandate: null,

      isResendingEmail: false,

      // données UI
      details: {},
      mandatePanel: [],
      history: [],
      historyFields: [
        { key: "status", label: this.$t('mandate.status_collection'), class: "text-center" },
        { key: "status_date", label: this.$t('mandate.date_action'), class: "text-center" }
      ],
      signedPdfBase64: "",
      signedPdfFileName: "",

      // Collections associées
      collections: [],
      collectionFields: [
        { key: "EndToEndId", label: "Identifier", class: "text-center" },
        { key: "DbtrIBAN", label: "IBAN", class: "text-center" },
        { key: "Amount", label: "Amount", class: "text-center" },
        { key: "DueDate", label: "Due Date", class: "text-center" },
        { key: "SettlementDate", label: "Settlement Date", class: "text-center" },
        { key: "DebitDate", label: "Debit Date", class: "text-center" },
        { key: "Status", label: "Status", class: "text-center" },
        { key: "RemittanceInfo", label: "Description", class: "text-center" },
        { key: "RejectionReason", label: "Rejection Reason", class: "text-center" }
      ],

      // mode édition
      isEditMode: false,
      editable: {
        UMR: "",
        // type: "",
        reason: "",
        lang: "",

        // Crediteur
        cdtr_name: "",
        cdtr_identifier: "",
        cdtr_ref: "",
        cdtr_addr: "",
        cdtr_city: "",
        cdtr_country: "",



        dbtr_name: "",
        dbtr_id: "",
        dbtr_addr: "",
        dbtr_city: "",
        dbtr_country: "",
        dbtr_iban: "",
        dbtr_bic: "",
        dbtr_email: ""
      },
      mandateLanguageOptions: [
        { code: 'DE', label: 'Deutsch' },
        { code: 'EN', label: 'English' },
        { code: 'ES', label: 'Español' },
        { code: 'FR', label: 'Français' },
        { code: 'IT', label: 'Italiano' },
        { code: 'PT', label: 'Português' }
      ],
      countryOptions: [
        { iso2: 'FR', key: 'france' },
        { iso2: 'DE', key: 'allemagne' },
        { iso2: 'IT', key: 'italie' },
        { iso2: 'ES', key: 'espagne' },
        { iso2: 'BE', key: 'belgique' },
        { iso2: 'LU', key: 'luxembourg' },
        { iso2: 'NL', key: 'pays_bas' },
        { iso2: 'PT', key: 'portugal' },
        { iso2: 'IE', key: 'irlande' },
        { iso2: 'AT', key: 'autriche' },
        { iso2: 'FI', key: 'finlande' },
        { iso2: 'SE', key: 'suede' },
        { iso2: 'DK', key: 'danemark' },
        { iso2: 'NO', key: 'norvege' },
        { iso2: 'IS', key: 'islande' },
        { iso2: 'CH', key: 'suisse' },
        { iso2: 'PL', key: 'pologne' },
        { iso2: 'CZ', key: 'republique_tcheque' },
        { iso2: 'SK', key: 'slovaquie' },
        { iso2: 'SI', key: 'slovenie' },
        { iso2: 'HR', key: 'croatie' },
        { iso2: 'EE', key: 'estonie' },
        { iso2: 'LV', key: 'lettonie' },
        { iso2: 'LT', key: 'lituanie' },
        { iso2: 'MT', key: 'malte' },
        { iso2: 'CY', key: 'chypre' },
        { iso2: 'RO', key: 'roumanie' },
        { iso2: 'BG', key: 'bulgarie' },
        { iso2: 'HU', key: 'hongrie' },
        { iso2: 'GR', key: 'grece' }
      ],
      rawMandate: null
    }
  },
  created () {
    this.IdMandate = this.$route.params.id_mandate || this.$route.params.id
    this.IdMandateBulk = this.$route.query.IdMandateBulk

    if (this.IdMandateBulk !== undefined) {
      this.returnUrl = '/mandatedetailbulk/' + this.IdMandateBulk + '?IdMandateBulk=' + this.IdMandateBulk
    } else {
      this.returnUrl = '/mandatpage/unit'
    }
    this.getDetailFromRoute()

    this.$bus?.$emit?.("change-mobile-header", {
      backLink: false,
      navbarInnerStyle: "justify-content:start;",
      navTitle: this.$t("common.entity_account.entity_account_details"),
    })
    this.$f7?.initPullToRefresh?.()
    this.$bus?.$emit?.("right-panel-set-feature", "filter-messages")
  },
  methods: {

    asEditable (val) {
      if (val == null) return ""
      const s = String(val).trim()
      if (s === "-" || s === "—" || s === "$NotFound") return ""
      return val
    },
    isValidEmail (email) {
      if (!email) return false
      const s = String(email).trim()
      // Regex simple et suffisante pour un front
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(s)
    },

    showError (message) {
      const text = message || this.$t?.('common.error') || 'Error'
      const title = this.$t?.('common.error') || 'Error'

      // Si Framework7 est dispo → joli modal
      if (this.$f7?.modal) {
        this.$f7.modal({
          title,
          text,
          buttons: [
            { text: 'OK', close: true }
          ]
        })
        return
        }
        // Fallback : alert navigateur
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(text)
        } else {
          console.error('[MandateDetail] ', text)
        }
      },
      // Demo only: the API call to resend the signed PDF will be added later.
      sendSignedPdfCopy () {
        // Intentionally left empty for now.
      },

      async resendSignatureEmail () {
        if (this.isResendingEmail) return

        // Toujours prioriser ce qui vient de mapApiMandateDetail (details),
        // avec fallback sur rawMandate (payload brut du GET)
        const id_mandate =
          this.details?.id_mandate ||
          this.rawMandate?.id_mandate ||
          this.IdMandate

        const UMR =
          (this.details?.UMR || '').trim() ||
          (this.rawMandate?.UMR || '').trim() ||
          (this.$route.params.UMR || '').trim() ||
          (this.$route.query.UMR || '').trim()

        const dbtr_email =
          (this.details?.dbtr_email || this.rawMandate?.dbtr_email || '').trim()

        const cdtr_name =
          (this.details?.cdtr_name || this.rawMandate?.cdtr_name || '').trim()

        // message_type récupéré de l’API
        const message_type =
          (this.details?.message_type || this.rawMandate?.message_type || '').trim()

        if (!id_mandate || !UMR) {
          this.showError('Missing mandate identifiers (id_mandate / UMR).')
          return
        }
        if (!dbtr_email) {
          this.showError('Debtor email is missing. Cannot resend signature email.')
          return
        }
        if (!cdtr_name) {
          this.showError('Creditor name is missing. Cannot resend signature email.')
          return
        }

        this.isResendingEmail = true
        this.activateSpinner = true

        try {
          const params = {
            id_mandate,
            UMR,
            dbtr_email,
            cdtr_name,

            // repris de l’API, pas en dur
            ...(message_type ? { message_type } : {}),

            // ce que tu voulais en plus
            mode: 'renvoie'
          }

          await this.$engine.Mandat.createMandate(params)

          this.$f7?.modal?.({
            title: 'Signature email sent',
            text: 'The signature email has been resent to the debtor.',
            buttons: [
              { text: 'OK', close: true }
            ]
          })
        } catch (e) {
          console.error(e)
          this.showError('Error while resending the signature email.')
        } finally {
          this.isResendingEmail = false
          this.activateSpinner = false
        }
      },


    isValidUMR (umr) {
      if (!umr) return false

      const value = umr.trim().toUpperCase()

      // Format : 2 lettres + 20 alphanumériques + _ + YYYY + _ + 5 alphanumériques
      const re = /^[A-Z]{2}[A-Z0-9]{20}-[0-9]{4}-[A-Z0-9]{5}$/
      return re.test(value)
    },

    /* ========= MODE EDITION ========= */

    enterEditMode () {
      this.initEditableFromDetails()
      this.isEditMode = true
    },

    cancelEdit () {
      // On annule juste les changements locaux
      this.initEditableFromDetails()
      this.isEditMode = false
    },

    iso2ToCountryName (code) {
      if (!code) return ''
      const c = String(code).trim().toUpperCase()
      const map = {
        FR: 'France',
        DE: 'Germany',
        IT: 'Italy',
        ES: 'Spain',
        BE: 'Belgium',
        LU: 'Luxembourg',
        NL: 'Netherlands',
        PT: 'Portugal',
        IE: 'Ireland',
        AT: 'Austria',
        FI: 'Finland',
        SE: 'Sweden',
        DK: 'Denmark',
        NO: 'Norway',
        IS: 'Iceland',
        CH: 'Switzerland',
        PL: 'Poland',
        CZ: 'Czech Republic',
        SK: 'Slovakia',
        SI: 'Slovenia',
        HR: 'Croatia',
        EE: 'Estonia',
        LV: 'Latvia',
        LT: 'Lithuania',
        MT: 'Malta',
        CY: 'Cyprus',
        RO: 'Romania',
        BG: 'Bulgaria',
        HU: 'Hungary',
        GR: 'Greece'
      }
      return map[c] || code // fallback: on renvoie le code brut si inconnu
    },


    mandateLanguageLabel (code) {
      const normalized = String(code || '').trim().toUpperCase()
      return (this.mandateLanguageOptions.find(option => option.code === normalized) || {}).label || normalized || '—'
    },

    initEditableFromDetails () {
      const d = this.details || {}
      const r = this.rawMandate || {}


      const clean = this.asEditable

      this.editable = {
        UMR: clean(d.UMR) || "",
        type: this.mapTypeToUi(clean(d.type), this.rawMandate),
        reason: clean(d.reason) || clean(r.reason)|| "",
        // Vide par défaut : ne pas envoyer lang si l'utilisateur ne choisit rien.
        lang: "",

        // Crediteur


        cdtr_name: clean(d.cdtr_name) || clean(r.cdtr_name) || "",
        cdtr_identifier: clean(d.cdtr_identifier) || clean(r.cdtr_identifier) || clean(r.cdtr_id) || "",
        cdtr_ref: clean(d.cdtr_ref) || clean(r.cdtr_ref) || "",
        cdtr_addr: clean(d.cdtr_addr) || clean(r.cdtr_addr) || clean(r.cdtr_address) || "",
        cdtr_city: clean(d.cdtr_city) || clean(r.cdtr_city) || "",
        cdtr_country: (clean(r.cdtr_country) || '').toUpperCase() || (clean(d.cdtr_country) || '').toUpperCase() ||'FR',
        //cdtr_country: clean(d.cdtr_country) || this.iso2ToCountryName(r.cdtr_country) || "",

        // Debiteur
        dbtr_name: clean(d.dbtr_name) || clean(r.dbtr_name) || "",
        dbtr_id: clean(d.dbtr_id) || clean(r.dbtr_id) || "",
        dbtr_addr: clean(d.dbtr_addr) || r.dbtr_addr || "",
        dbtr_city: clean(d.dbtr_city) || r.dbtr_city || "",
        dbtr_country: clean(d.dbtr_country) || this.iso2ToCountryName(r.dbtr_country) || "",
        dbtr_iban: clean(d.dbtr_iban) || r.dbtr_iban || "",
        dbtr_bic: clean(d.dbtr_bic) || r.dbtr_bic || "",
        dbtr_email: clean(d.dbtr_email) || r.dbtr_email || ""

      }
    },

    onCdtrCountryChange () {
      // pays (texte complet) choisi dans le select
      const countryFullName = (this.editable.cdtr_country || '').trim()
      const cc = (this.editable.cdtr_country || '').trim().toUpperCase()

      // on prend comme "identifiant national" ce que renvoie l’API brut,
      // ou à défaut l’identifiant affiché
      const nationalIdRaw =
        (this.rawMandate && (this.rawMandate.cdtr_identifier || this.rawMandate.cdtr_id)) ||
        this.details.cdtr_identifier ||
        this.editable.cdtr_identifier ||
        ''

      //const newICS = this.computeICSFrom(countryFullName, nationalIdRaw, 'ZZZ')
      const newICS = this.computeICSFromISO2(cc, nationalIdRaw, 'ZZZ')

      if (newICS) {
        // on met l’identifiant recalculé dans le champ éditable
        this.editable.cdtr_identifier = newICS
      } else {
        // si on ne peut pas calculer, à toi de voir :
        // soit on laisse l’ancien, soit on vide. Ici je laisse l’ancien.
        console.warn('[onCdtrCountryChange] Impossible de recalculer ICS')
      }
    },


    confirmSendEdit () {
      const id_mandate = this.details?.id_mandate || this.IdMandate

      const originalUMR = (this.details?.UMR || '').trim().toUpperCase()
      let UMR = (this.editable?.UMR || originalUMR).trim().toUpperCase()

      if (!id_mandate || !UMR) {
        this.showError(this.$t?.('mandate.error_umr') || 'Missing mandate identifiers (id_mandate / UMR).')
        return
      }

      // 📨 Email débiteur : obligatoire + format
      const email = (this.editable?.dbtr_email || '').trim()
      if (!email) {
        this.showError(this.$t?.('mandate.dbtr_email_required') || 'Debtor email is required.')
        return
      }
      if (!this.isValidEmail(email)) {
        this.showError(this.$t?.('mandate.dbtr_email_invalid') || 'Debtor email format is invalid.')
        return
      }

      // On ne valide le format de l’UMR QUE si l'utilisateur l’a modifiée
      if (UMR !== originalUMR) {
        if (!this.isValidUMR(UMR)) {
          this.showError(
            this.$t?.('mandate.invalid_umr_format') ||
            'Invalid UMR format. Expected: CC + 20 alphanumeric + _YYYY_ + 5 alphanumeric.'
          )
          return
        }
        this.editable.UMR = UMR
      } else {
        UMR = originalUMR
      }

      this.$f7?.modal?.({
        title: this.$t('mandate.edit_confirm_title') || 'Confirm changes',
        text: this.$t('mandate.edit_confirm_text') || 'Are you sure you want to submit these changes?',
        buttons: [
          { text: 'No', close: true },
          {
            text: 'Yes',
            bold: true,
            onClick: () => this.onConfirmSendEdit({ id_mandate, UMR })
          }
        ]
      })
    },

    async onConfirmSendEdit ({ id_mandate, UMR }) {
      this.activateSpinner = true
      try {
        const d = this.details || {}
        const e = this.editable || {}
        const r = this.rawMandate || {}

        const txType = this.mapUiTypeToApi(e.type || d.type, r.type_transaction)

        // const countryFullName =
        //   e.cdtr_country ||
        //   d.cdtr_country ||
        //   this.iso2ToCountryName(r.cdtr_country) ||
        //   ""

        //const cdtrCtryISO2 = this.countryNameToISO2(countryFullName)
        let cdtrCtryISO2 = (e.cdtr_country || '').trim().toUpperCase()

        // si jamais e.cdtr_country contient encore un nom
        if (!/^[A-Z]{2}$/.test(cdtrCtryISO2)) {
          cdtrCtryISO2 =
            this.countryNameToISO2(e.cdtr_country) ||
            (r.cdtr_country || '').trim().toUpperCase() ||
            ''
        }

        // fallback ultime
        if (!/^[A-Z]{2}$/.test(cdtrCtryISO2)) cdtrCtryISO2 = 'FR'

        let MessageType = ''
        if (r.signing_md_date === "") {
          MessageType = "Issuing"
        } else {
          MessageType = "Amendment"
        }

        const params = {
          id_mandate,
          UMR,

          id_contract: r.id_contract || "",
          type_transaction: txType,
          reason_mandate: e.reason || d.reason || r.reason_mandate || "",
          message_type: MessageType,
          schema_code: r.schema_code || "",

          cdtr_name: e.cdtr_name || d.cdtr_name || r.cdtr_name || "",
          cdtr_id: e.cdtr_identifier || d.cdtr_identifier || r.cdtr_identifier || r.cdtr_id || "",
          cdtr_ref: e.cdtr_ref || d.cdtr_ref || r.cdtr_ref || "",
          cdtr_addr: e.cdtr_addr || d.cdtr_addr || r.cdtr_addr || r.cdtr_address || "",
          cdtr_city: e.cdtr_city || d.cdtr_city || r.cdtr_city || "",
          cdtr_ctry: cdtrCtryISO2 || r.cdtr_country || "",

          // Debiteur : on garde les valeurs backend (lecture seule)
          dbtr_name: d.dbtr_name || r.dbtr_name || "",
          dbtr_id: d.dbtr_id || r.dbtr_id || "",
          dbtr_addr: d.dbtr_addr || r.dbtr_addr || "",
          dbtr_city: d.dbtr_city || r.dbtr_city || "",
          dbtr_country: d.dbtr_country || r.dbtr_country || "",
          dbtr_iban: d.dbtr_iban || r.dbtr_iban || "",
          dbtr_bic: d.dbtr_bic || r.dbtr_bic || "",

          // 📨 ici on utilise bien l’email édité et déjà validé
          dbtr_email: (e.dbtr_email || d.dbtr_email || r.dbtr_email || "").trim()
        }

        // Inclure lang seulement si une nouvelle langue a été sélectionnée.
        const selectedLang = String(e.lang || '').trim().toUpperCase()
        const currentLang = String(r.lang || r.language || d.lang || '').trim().toUpperCase()
        if (selectedLang && selectedLang !== currentLang) {
          params.lang = selectedLang
        }

        await this.editMandate(params)

        this.isEditMode = false

        this.$f7?.modal?.({
          title: this.$t('mandate.edited_success') || 'Mandate updated',
          text: this.$t('mandate.edited_success_text') || 'The mandate has been successfully updated.',
          buttons: [
            {
              text: 'OK',
              onClick: () => {
                this.getDetailFromRoute()
              }
            }
          ]
        })
      } catch (e) {
        console.error(e)
        this.showError(this.$t?.('common.error') || 'Error while updating the mandate.')
      } finally {
        this.activateSpinner = false
      }
    },

    editMandate (params) {
      // même principe que deleteMandate / createMandate
      return this.$engine.Mandat.editMandate(params)
    },

    /* ========= CANCEL MANDATE EXISTANT ========= */

    confirmCancelMandate () {
      const id_mandate = this.details?.id_mandate || this.IdMandate
      const UMR = this.details?.UMR || this.$route.params.UMR || this.$route.query.UMR

      if (!id_mandate || !UMR) {
        this.showError(this.$t?.('common.error') || 'Missing mandate identifiers (id_mandate / UMR).')
        return
      }

      this.$f7?.modal?.({
        title: this.$t('mandate.cancel_confirm_title') || 'Cancel mandate',
        text: this.$t('mandate.cancel_confirm_text') || 'Are you sure you want to cancel this mandate?',
        buttons: [
          { text: 'No', close: true },
          {
            text: 'Yes',
            bold: true,
            onClick: () => this.onConfirmCancelMandate({ id_mandate, UMR })
          }
        ]
      })
    },

    async onConfirmCancelMandate ({ id_mandate, UMR }) {
      this.activateSpinner = true
      try {
        await this.deleteMandate({ id_mandate, UMR })
        this.$f7?.modal?.({
          title: this.$t('mandate.cancelled_success') || 'Mandate cancelled',
          text: this.$t('mandate.cancelled_success_text') || 'The mandate has been successfully cancelled.',
          buttons: [
            {
              text: 'OK',
              onClick: () => {
                loadPage(this, { url: '/mandatpage/unit' })
              }
            }
          ]
        })
      } catch (e) {
        console.error(e)
        this.showError(this.$t?.('common.error') || 'Error while cancelling the mandate.')
      } finally {
        this.activateSpinner = false
      }
    },

    onRefresh () {
    // Si tu veux remettre le spinner
      this.activateSpinner = true
      // Si tu veux sortir du mode édition au passage
      this.isEditMode = false

      this.getDetailFromRoute()
    },


    deleteMandate (params) {
      return this.$engine.Mandat.deleteMandate(params)
    },

    /* ========= GET DETAILS  ========= */

    getDetailFromRoute () {
      const rawId = this.$route.params.id_mandate || this.$route.params.id || this.IdMandate
      if (!rawId) return
      const isNumericId = /^[0-9]+$/.test(String(rawId))
      const params = isNumericId ? { id_mandate: rawId } : { UMR: rawId }
      params.IdMandateBulk = this.IdMandateBulk
      this.getDetail(params)
    },

    getDetail (params) {
      this.activateSpinner = true
      this.$engine.Mandat.getDetails(params)
        .then((resp) => {
          const payload = (resp && resp.data) || {}

          let m = null
          if (Array.isArray(payload.Mandates) && payload.Mandates.length) {
            const byId = params?.id_mandate != null
            m = payload.Mandates.find(x =>
              byId
                ? String(x?.id_mandate) === String(params.id_mandate)
                : String(x?.UMR || "") === String(params.UMR || "")
            ) || payload.Mandates[0]
          } else {
            m = payload.Mandate ?? payload
          }

          const mapped = this.mapApiMandateDetail(m, params)
          this.rawMandate = m || null
          this.updateDetail(mapped)

          const idMandate = mapped?.details?.id_mandate || m?.id_mandate
          if (idMandate) {
            this.getCollections(idMandate)
          }

          this.$f7?.pullToRefreshDone?.()
          this.activateSpinner = false
        })
        .catch(() => {
          this.$f7?.pullToRefreshDone?.()
          this.activateSpinner = false
          this.showError(this.$t?.("common.error") || "Error while loading details")
        })
    },

    getCollections (IdMandate) {
      if (!IdMandate) return
      const params = { IdMandate }
      this.$engine.Collection.getCollection(params)
        .then((resp) => {
          const payload = (resp && resp.data) || {}
          const rawList = Array.isArray(payload.Collection) ? payload.Collection : []
          const filteredList = rawList
            .filter(c => String(c.IdMandate) === String(IdMandate))
            .sort((a, b) => {
              const dateA = new Date(String(a.DueDate || '').replace(' ', 'T'))
              const dateB = new Date(String(b.DueDate || '').replace(' ', 'T'))
              return dateB - dateA
            })
          this.collections = filteredList.map(c => ({
            EndToEndId: c.EndToEndId || "—",
            DbtrIBAN: c.DbtrIBAN || "—",
            Amount: this.formatCurrencyCollection(c.Amount),
            DueDate: this.formatDateShortCollection(c.DueDate),
            SettlementDate: this.formatDateShortCollection(c.SettlementDate),
            DebitDate: this.formatDateShortCollection(c.DebitDate),
            Status: this.collectionStatusLabel(c.Status),
            RemittanceInfo: c.RemittanceInfo || "—",
            RejectionReason: c.RejectionReason || "—"
          }))
        })
        .catch((e) => {
          console.error("[getCollections] error", e)
          this.collections = []
        })
    },

    formatCurrencyCollection (n) {
      if (n === "" || n === null || typeof n === "undefined") return "—"
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(Number(n))
    },

    formatDateShortCollection (dateIso) {
      if (!dateIso) return "—"
      const s = String(dateIso)
      if (s.includes("T")) return s.split("T")[0]
      if (s.includes(" ")) return s.split(" ")[0]
      return s
    },

    collectionStatusLabel (code) {
      const map = {
        "1": "Pending",
        "2": "Collected",
        "3": "Rejected",
        "4": "Cancelled",
        "5": "Reversed",
        "6": "Disputed",
        "7": "Settled",
        "8": "On hold",
        "9": "Refunded",
        "10": "Error"
      }
      const key = code != null ? String(code) : ""
      return map[key] || "—"
    },

    updateDetail ({ details, mandatePanel, history, signedPdfBase64, signedPdfFileName }) {
      this.details = details
      this.mandatePanel = mandatePanel
      this.history = history
      this.signedPdfBase64 = signedPdfBase64 || ""
      this.signedPdfFileName = signedPdfFileName || "mandate_signed.pdf"
    },

    mapApiMandateDetail (m, params) {
      const ymdDate = this.formatIsoToYmdDateOnly
      const ymdDateTime = this.formatIsoToYmd
      const statusLabel = this.statusCodeToLabel
      // valeur brute venant de l’API
      const rawCdtrRef = m?.cdtr_ref_name || ""

      // normalisation : si vide ou "$NotFound" → "-"
      const cdtr_ref =
        !rawCdtrRef || rawCdtrRef === "$NotFound"
          ? "-"
          : rawCdtrRef

      const details = {
        UMR: m?.UMR || params?.UMR || "-",
        id_mandate: m?.id_mandate || params?.id_mandate || "-",
        status: statusLabel(m?.status),
        status_code: (m && m.status != null) ? String(m.status).trim() : "",
        reject_reason: m?.reject_reason || "",
        type: m?.type || m?.type_transaction || "-",
        reason: m?.reason_mandate || "-",
        lang: (m?.lang || m?.language || '').toUpperCase(),
        message_type: m?.message_type || "-",
        signed_date: ymdDate(m?.signing_md_date || "-"),
        cancelled_date: ymdDate(m?.signing_cancel_date || "-"),
        creation_date: ymdDate(m?.creation_date || "-"),
        cdtr_name: m?.cdtr_name || "-",
        cdtr_identifier: m?.cdtr_identifier || m?.cdtr_id || "-",
        id_contract: m?.id_contract || "-",
        // cdtr_ref: m?.cdtr_ref_name || m?.id_contract || "-",
        cdtr_ref,
        cdtr_addr: m?.cdtr_addr || m?.cdtr_address || "-",
        cdtr_city: m?.cdtr_city || "-",
        // cdtr_country: m?.cdtr_country || "-",
        dbtr_name: m?.dbtr_name || "-",
        cdtr_country: this.iso2ToCountryName(m?.cdtr_country) || "-",
        dbtr_country: this.iso2ToCountryName(m?.dbtr_country) || "-",
        dbtr_id: m?.dbtr_id || "-",
        dbtr_addr: m?.dbtr_addr || "-",
        dbtr_city: m?.dbtr_city || "-",
        //dbtr_country: m?.dbtr_country || "-",
        dbtr_iban: m?.dbtr_iban || "-",
        dbtr_bic: m?.dbtr_bic || "-",
        dbtr_email: m?.dbtr_email || ""
      }

      const mandatePanel = [
        { title: "Unique Mandate Identifier(UMR)", value: details.UMR || "—", color: "#000" },
        { title: "Status", value: details.status || "—", color: "var(--actionButtonColorBlue)" },
        { title: "Type of collections", value: details.type || "—", color: "#000" },
        { title: "Signed", value: details.signed_date || "—", color: "#000" },
        { title: "Cancelled", value: details.cancelled_date || "—", color: "#000" }
      ]

      const rawHistory = Array.isArray(m?.History) ? m.History : []
      const history = rawHistory.length
        ? rawHistory.map(h => ({
            status: statusLabel(h?.status ?? m?.status),
            status_date: ymdDateTime(h?.creation_date ?? m?.creation_date)   // garde l’heure
          }))
        : [{
            status: details.status || "—",
            status_date: details.creation_date || "—"   // ici déjà sans heure, c’est normal
          }]

      const rawDoc = (m?.document || "").toString().trim()
      const signedPdfBase64 = rawDoc
        ? rawDoc
            .replace(/^data:application\/pdf;base64,/, "")
            .replace(/\s/g, "")
        : ""

      const signedPdfFileName =
        m?.document_name ||
        `${(details.creation_date || "").slice(0,10) || "mandate"}_${details.UMR || "signed"}.pdf`

      return { details, mandatePanel, history, signedPdfBase64, signedPdfFileName }
    },

    statusCodeToLabel (code) {
      const key = code != null ? String(code).trim() : ""
      if (!key) return "—"
      const i18nPath = `mandate_details.hist_status.status_${key}`
      if (this.$te ? this.$te(i18nPath) : true) {
        const translated = this.$t(i18nPath)
        if (translated && translated !== i18nPath) return translated
      }
      return key || "Pending"
    },

    formatIsoToYmd (val) {
      if (!val) return ""
      const d = new Date(String(val).replace(" ", "T"))
      if (isNaN(d)) return ""
      const pad = n => String(n).padStart(2, "0")
      return (
        `${d.getFullYear()}-` +
        `${pad(d.getMonth() + 1)}-` +
        `${pad(d.getDate())} ` +
        `${pad(d.getHours())}:` +
        `${pad(d.getMinutes())}:` +
        `${pad(d.getSeconds())}`
      )
    },
    formatIsoToYmdDateOnly (val) {
      if (!val) return ""
      const d = new Date(String(val).replace(" ", "T"))
      if (isNaN(d)) return ""
      const pad = n => String(n).padStart(2, "0")
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },

    downloadSignedPdf () {
      try {
        if (!this.signedPdfBase64) {
          this.$f7?.dialog?.alert(
            this.$t('mandate.no_pdf') || 'Aucun document disponible.',
            this.$t('common.error') || 'Erreur'
          )
          return
        }

        const cleanBase64 = this.signedPdfBase64
          .replace(/^data:application\/pdf;base64,/, '')
          .replace(/\s/g, '')

        let byteCharacters
        try {
          byteCharacters = atob(cleanBase64)
        } catch (err) {
          console.error('Base64 invalide pour le PDF :', err)
          this.$f7?.dialog?.alert(
            this.$t('mandate.invalid_pdf') || 'Le document PDF reçu est invalide (base64).',
            this.$t('common.error') || 'Erreur'
          )
          return
        }

        const byteArray = new Uint8Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i)
        }

        const blob = new Blob([byteArray], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = this.signedPdfFileName || 'mandate_signed.pdf'
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        setTimeout(() => {
          const win = window.open(url, '_blank')
          if (!win || win.closed || typeof win.closed === 'undefined') {
            console.warn('[downloadSignedPdf] Impossible d’ouvrir dans un nouvel onglet')
          }
        }, 500)

        setTimeout(() => {
          URL.revokeObjectURL(url)
        }, 200000)
      } catch (e) {
        console.error('Erreur lors du téléchargement du PDF signé', e)
        this.$f7?.dialog?.alert(
          this.$t('errors.generic') || 'Une erreur est survenue lors du téléchargement.',
          this.$t('common.error') || 'Erreur'
        )
      }
    },


    addAuditPdfHeader (doc) {
      const pageWidth = doc.internal.pageSize.getWidth()

      doc.setFont("helvetica", "bold")
      doc.setFontSize(18)
      doc.setTextColor(55, 55, 55)
      doc.text("Mandate Details", 14, 16)

      doc.setFont("helvetica", "normal")
      doc.setFontSize(9)
      doc.setTextColor(105, 105, 105)
      doc.text(
        `UMR: ${this.details?.UMR || "—"}`,
        14,
        23
      )

      doc.setDrawColor(92, 171, 174)
      doc.setLineWidth(0.7)
      doc.line(14, 27, pageWidth - 14, 27)

      doc.setTextColor(0, 0, 0)
    },

    addAuditPdfFooter (doc) {
      const totalPages = doc.getNumberOfPages()

      for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page)

        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        doc.setDrawColor(220, 220, 220)
        doc.setLineWidth(0.2)
        doc.line(14, pageHeight - 13, pageWidth - 14, pageHeight - 13)

        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor(120, 120, 120)

        doc.text(
          `Generated ${moment().format("YYYY-MM-DD HH:mm:ss")}`,
          14,
          pageHeight - 7
        )

        doc.text(
          `Page ${page} / ${totalPages}`,
          pageWidth - 14,
          pageHeight - 7,
          { align: "right" }
        )
      }
    },

    auditPdfSectionTitle (doc, title, y) {
      const pageWidth = doc.internal.pageSize.getWidth()

      doc.setFont("helvetica", "bold")
      doc.setFontSize(11)
      doc.setTextColor(64, 150, 153)
      doc.text(title, 14, y)

      doc.setDrawColor(220, 230, 230)
      doc.setLineWidth(0.2)
      doc.line(14, y + 3, pageWidth - 14, y + 3)

      doc.setTextColor(0, 0, 0)
      return y + 8
    },

    addAuditInfoGrid (doc, title, items, startY) {
      const pageHeight = doc.internal.pageSize.getHeight()

      if (startY > pageHeight - 45) {
        doc.addPage("a4", "landscape")
        this.addAuditPdfHeader(doc)
        startY = 36
      }

      let y = this.auditPdfSectionTitle(doc, title, startY)

      const rows = []
      for (let i = 0; i < items.length; i += 3) {
        rows.push(items.slice(i, i + 3))
      }

      autoTable(doc, {
        startY: y,
        body: rows.map(row => {
          const cells = row.map(item => ({
            content: `${item.label}\n${item.value || "—"}`,
            styles: {
              cellPadding: 4,
              minCellHeight: 17
            }
          }))

          while (cells.length < 3) {
            cells.push({ content: "", styles: { minCellHeight: 17 } })
          }
          return cells
        }),
        theme: "plain",
        styles: {
          fontSize: 8.5,
          textColor: [55, 55, 55],
          fillColor: [249, 250, 250],
          lineColor: [235, 238, 238],
          lineWidth: 0.1,
          valign: "middle",
          overflow: "linebreak"
        },
        columnStyles: {
          0: { cellWidth: 89 },
          1: { cellWidth: 89 },
          2: { cellWidth: 89 }
        },
        margin: { left: 14, right: 14 },
        didParseCell: data => {
          if (!data.cell.text || !data.cell.text.length) return
          // Le premier texte est le libellé, la valeur reste sur la ligne suivante.
          // autoTable ne permet pas deux styles dans une cellule sans hook de dessin,
          // donc on garde une présentation sobre et proche de l'IHM.
        }
      })

      return doc.lastAutoTable.finalY + 7
    },

    async exportAuditTrailPdf () {
      try {
        if (!this.details || !this.details.UMR) {
          this.showError("Mandate details are not loaded.")
          return
        }

        this.activateSpinner = true
        this.$f7?.showPreloader?.(
          this.$i18n?.t?.("common.loading_page_label") || "Loading..."
        )

        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4"
        })

        this.addAuditPdfHeader(doc)

        let y = 36

        y = this.addAuditInfoGrid(
          doc,
          "Mandate Information",
          [
            { label: "Unique Mandate Reference", value: this.details.UMR || "—" },
            {
              label: "Transaction type",
              value: this.details.type
                ? this.$t("mandate_details.type_transaction_" + this.details.type)
                : "—"
            },
            { label: "Status of mandate", value: this.details.status || "—" },
            { label: "Creation date", value: this.details.creation_date || "—" },
            { label: "Signature date", value: this.details.signed_date || "—" },
            { label: "Cancel date", value: this.details.cancelled_date || "—" },
            { label: "Underlying Contract ID", value: this.details.id_contract || "—" },
            { label: "Message type", value: this.details.message_type || "—" },
            { label: "Reason", value: this.details.reason || "—" }
          ],
          y
        )

        y = this.addAuditInfoGrid(
          doc,
          "Creditor information",
          [
            { label: "Name", value: this.details.cdtr_name || "—" },
            { label: "Identifier", value: this.details.cdtr_identifier || "—" },
            { label: "Creditor Reference Party name", value: this.details.cdtr_ref || "—" },
            { label: "Address", value: this.details.cdtr_addr || "—" },
            { label: "City", value: this.details.cdtr_city || "—" },
            { label: "Country", value: this.details.cdtr_country || "—" }
          ],
          y
        )

        y = this.addAuditInfoGrid(
          doc,
          "Debtor information",
          [
            { label: "Email", value: this.details.dbtr_email || "—" },
            { label: "Name", value: this.details.dbtr_name || "—" },
            { label: "IBAN", value: this.details.dbtr_iban || "—" },
            { label: "Identifier", value: this.details.dbtr_id || "—" },
            { label: "Address", value: this.details.dbtr_addr || "—" },
            { label: "City", value: this.details.dbtr_city || "—" },
            { label: "Country", value: this.details.dbtr_country || "—" },
            {
              label: "Signature",
              value: this.signedPdfBase64 ? "Signed document available" : "—"
            }
          ],
          y
        )

        // History
        if (y > 155) {
          doc.addPage("a4", "landscape")
          this.addAuditPdfHeader(doc)
          y = 36
        }

        y = this.auditPdfSectionTitle(doc, "Mandate history", y)

        autoTable(doc, {
          startY: y,
          head: [["Status", "Action Date"]],
          body: (this.history && this.history.length)
            ? this.history.map(h => [
                h.status || "—",
                h.status_date || "—"
              ])
            : [["No history available", "—"]],
          theme: "striped",
          styles: {
            fontSize: 8.5,
            cellPadding: 2.8,
            halign: "center",
            valign: "middle"
          },
          headStyles: {
            fillColor: [92, 171, 174],
            textColor: [255, 255, 255],
            fontStyle: "bold"
          },
          alternateRowStyles: {
            fillColor: [220, 238, 238]
          },
          margin: { left: 14, right: 14 }
        })

        y = doc.lastAutoTable.finalY + 8

        // Collections
        if (y > 150) {
          doc.addPage("a4", "landscape")
          this.addAuditPdfHeader(doc)
          y = 36
        }

        y = this.auditPdfSectionTitle(doc, "Collections", y)

        autoTable(doc, {
          startY: y,
          head: [[
            "Identifier",
            "IBAN",
            "Amount",
            "Due Date",
            "Settlement Date",
            "Debit Date",
            "Status",
            "Description",
            "Rejection Reason"
          ]],
          body: (this.collections && this.collections.length)
            ? this.collections.map(c => [
                c.EndToEndId || "—",
                c.DbtrIBAN || "—",
                c.Amount || "—",
                c.DueDate || "—",
                c.SettlementDate || "—",
                c.DebitDate || "—",
                c.Status || "—",
                c.RemittanceInfo || "—",
                c.RejectionReason || "—"
              ])
            : [["No collections found", "—", "—", "—", "—", "—", "—", "—", "—"]],
          theme: "striped",
          styles: {
            fontSize: 7,
            cellPadding: 2,
            halign: "center",
            valign: "middle",
            overflow: "linebreak"
          },
          headStyles: {
            fillColor: [92, 171, 174],
            textColor: [255, 255, 255],
            fontStyle: "bold"
          },
          alternateRowStyles: {
            fillColor: [220, 238, 238]
          },
          columnStyles: {
            0: { cellWidth: 45 },
            1: { cellWidth: 43 },
            2: { cellWidth: 20 },
            3: { cellWidth: 24 },
            4: { cellWidth: 26 },
            5: { cellWidth: 24 },
            6: { cellWidth: 22 },
            7: { cellWidth: 30 },
            8: { cellWidth: 28 }
          },
          margin: { left: 14, right: 14 }
        })

        this.addAuditPdfFooter(doc)

        const safeUmr = String(this.details.UMR || "mandate")
          .replace(/[^a-zA-Z0-9_-]/g, "_")

        const fileName =
          `mandate_details_${safeUmr}_${moment().format("YYYYMMDD_HHmmss")}.pdf`

        doc.save(fileName)

        this.$f7?.alert?.(
          "",
          this.$i18n?.t?.("common.exprt_succ") || "Export successful"
        )
      } catch (e) {
        console.error("[exportAuditTrailPdf]", e)
        this.showError("Error while generating the mandate Audit Trail PDF.")
      } finally {
        this.$f7?.hidePreloader?.()
        this.activateSpinner = false
      }
    },

    newPaymentSchedule () {
    const idMandate = this.details.id_mandate
    const UMR = this.details.UMR
    const debtor = this.details.dbtr_name

    if (this.details && idMandate && UMR && debtor) {
      loadPage(this, {
        url: '/payment-schedules/new/unit/' + '?idMandate=' + idMandate + '&UMR=' + UMR + '&debtor=' + debtor
      })
      this.$bus.$emit('switch-selected-menu', '/payment-schedules/unit')
    }
},

    mapTypeToUi (val, raw) {
      // val = details.type (peut être 1, 2, '1', '2', 'one_off', 'recurrent', etc.)
      // raw = this.rawMandate (pour récupérer type_transaction si besoin)
      const src = val != null && val !== '-' ? val : (raw && raw.type_transaction)
      const s = String(src || '').toLowerCase()

      if (s === '1' || s === 'one_off' || s === 'one-off') return 'one_off'
      if (s === '2' || s === 'recurrent') return 'recurrent'
      return ''
    },

    mapUiTypeToApi (uiVal, rawType) {
      // uiVal = 'one_off' | 'recurrent' | '' (si pas changé)
      if (uiVal === 'one_off') return 1
      if (uiVal === 'recurrent') return 2

      // fallback : garder ce que l’API avait déjà
      if (rawType != null && rawType !== '') return rawType
      return ''
    },

    newTransaction() {
    const IdMandate = this.details.id_mandate
    const UMR = this.details.UMR
    const CdtrName = this.details.cdtr_name
    if (this.details && IdMandate && UMR) {
      loadPage(this, {
        url: '/formpagesdd/cdtr/unit/' + '?IdMandate=' + IdMandate + '&UMR=' + UMR + '&CdtrName=' + CdtrName
      })
      this.$bus.$emit('switch-selected-menu', '/mandatetransactions/unit')
    }
  },

    countryNameToISO2 (name) {
      if (!name) return ''
      const n = name.trim().toLowerCase()
      const map = {
        'france': 'FR',
        'allemagne': 'DE', 'germany': 'DE',
        'italie': 'IT', 'italy': 'IT',
        'espagne': 'ES', 'spain': 'ES',
        'belgique': 'BE', 'belgium': 'BE',
        'luxembourg': 'LU',
        'pays-bas': 'NL', 'pays bas': 'NL', 'netherlands': 'NL',
        'portugal': 'PT',
        'irlande': 'IE', 'ireland': 'IE',
        'autriche': 'AT', 'austria': 'AT',
        'finlande': 'FI', 'finland': 'FI',
        'suède': 'SE', 'suede': 'SE', 'sweden': 'SE',
        'danemark': 'DK', 'denmark': 'DK',
        'norvège': 'NO', 'norvege': 'NO', 'norway': 'NO',
        'islande': 'IS', 'iceland': 'IS',
        'suisse': 'CH', 'switzerland': 'CH',
        'pologne': 'PL', 'poland': 'PL',
        'république tchèque': 'CZ', 'republique tcheque': 'CZ', 'czechia': 'CZ', 'czech republic': 'CZ',
        'slovaquie': 'SK', 'slovakia': 'SK',
        'slovénie': 'SI', 'slovenie': 'SI', 'slovenia': 'SI',
        'croatie': 'HR', 'croatia': 'HR',
        'estonie': 'EE', 'estonia': 'EE',
        'lettonie': 'LV', 'latvia': 'LV',
        'lituanie': 'LT', 'lithuania': 'LT',
        'malte': 'MT', 'malta': 'MT',
        'chypre': 'CY', 'cyprus': 'CY',
        'roumanie': 'RO', 'romania': 'RO',
        'bulgarie': 'BG', 'bulgaria': 'BG',
        'hongrie': 'HU', 'hungary': 'HU',
        'grèce': 'GR', 'grece': 'GR', 'greece': 'GR'
      }
      return map[n] || ''
    },

    normalizeNationalId (cc, raw) {
      if (!raw) return { nid: '', note: '' }

      let nid = String(raw).replace(/\s+/g, '')
      let note = ''

      const maybeIcs = String(raw).trim().toUpperCase()
      if (/^[A-Z]{2}\d{2}[A-Z0-9]{3}\d{15}$/.test(maybeIcs)) {
        // ICS -> on récupère les 15 digits de fin
        const last15 = maybeIcs.slice(-15)
        // pour la France, on prend le SIREN = les 9 derniers
        return { nid: last15.slice(-9), note: 'ICS détecté, extraction SIREN depuis les 15 digits' }
      }

      if (cc === 'FR') {
        const digits = nid.replace(/\D/g, '')

        // 1) SIRET (14 chiffres) → on prend le SIREN (9 premiers)
        if (/^\d{14}$/.test(digits)) {
          nid = digits.slice(0, 9)
          note = `SIRET détecté, SIREN utilisé: ${nid}`
          return { nid, note }
        }

        // 2) SIREN (9 chiffres)
        if (/^\d{9}$/.test(digits)) {
          nid = digits
          note = `SIREN utilisé: ${nid}`
          return { nid, note }
        }

        // 3) Sinon : on tombe sur le fallback générique (comme les autres pays)
        nid = nid.toUpperCase()
        if (/[^A-Z0-9]/.test(nid)) {
          return { nid: '', note: 'Identifiant national FR invalide (non alphanumérique)' }
        }
        note = 'Identifiant FR non standard, utilisé tel quel'
        return { nid, note }
      }

      // Pays autres que FR : comportement actuel
      nid = nid.toUpperCase()
      if (/[^A-Z0-9]/.test(nid)) return { nid: '', note: 'Identifiant national non alphanumérique' }

      return { nid, note }
    },

    lettersToDigits (s) {
      let out = ''
      for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i)
        if (code >= 48 && code <= 57) out += s[i]
        else if (code >= 65 && code <= 90) out += (code - 55)
      }
      return out
    },

    mod97 (numStr) {
      let rem = 0
      for (let i = 0; i < numStr.length; i += 7) {
        const part = String(rem) + numStr.substr(i, 7)
        rem = parseInt(part, 10) % 97
      }
      return rem
    },

    computeICSFromISO2 (cc, nationalIdRaw, businessCode = 'ZZZ') {
      const iso2 = (cc || '').trim().toUpperCase()
      if (!/^[A-Z]{2}$/.test(iso2)) return ''

      const { nid } = this.normalizeNationalId(iso2, nationalIdRaw)
      if (!nid) return ''

      const nidDigits = String(nid).replace(/\D/g, '')
      const nidPadded15 = nidDigits.padStart(15, '0')

      const b = (businessCode || 'ZZZ').toUpperCase()
      if (!/^[A-Z0-9]{3}$/.test(b)) return ''

      const rearr = `${b}${nidPadded15}${iso2}00`
      const numeric = this.lettersToDigits(rearr)
      const remainder = this.mod97(numeric)
      const check = String(98 - remainder).padStart(2, '0')

      return `${iso2}${check}${b}${nidPadded15}`
    },

    computeICSFrom (countryFullName, nationalIdRaw, businessCode = 'ZZZ') {
      const cc = this.countryNameToISO2(countryFullName)
      if (!cc) return ''

      const { nid } = this.normalizeNationalId(cc, nationalIdRaw)
      if (!nid) return ''

      const nidDigits = String(nid).replace(/\D/g, '')
      const nidPadded15 = nidDigits.padStart(15, '0')

      const b = (businessCode || 'ZZZ').toUpperCase()
      if (!/^[A-Z0-9]{3}$/.test(b)) return ''

      const rearr = `${b}${nidPadded15}${cc}00`
      const numeric = this.lettersToDigits(rearr)
      const remainder = this.mod97(numeric)
      const check = String(98 - remainder).padStart(2, '0')

      return `${cc}${check}${b}${nidPadded15}`
    }
  },
  computed: {
    txTypeOptions () {
      return [
        {
          value: 'one_off',
          text: this.$t('mandate.one_off') || 'One-off'
        },
        {
          value: 'recurrent',
          text: this.$t('mandate.recurrent') || 'Recurrent'
        }
      ]
    },
    topStatusCode () {
      const fromDetails = this.details && this.details.status_code
      const fromRaw = this.rawMandate && this.rawMandate.status
      return (fromDetails != null && String(fromDetails).trim()) ||
             (fromRaw != null && String(fromRaw).trim()) ||
             ''
    },

    showAddButtons () {
      const code = this.topStatusCode
      return code === '5'
    },

    showResendSignatureButton () {
      const code = this.topStatusCode
      return code === '0' || code === '6'
    },

    // si status = 7 ou 10 -> plus de boutons Edit / Cancel
    canEditOrCancel () {
      const raw =
        (this.details && this.details.status_code) ||
        (this.rawMandate && this.rawMandate.status)

      const code = raw != null ? String(raw).trim() : ''
      return code !== '7' || code !== '10'
    }
  }
}
</script>
